import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, ArrowRight, Plane, Crown, Car, CalendarCheck } from 'lucide-react';
import VehicleImage from './VehicleImage';
import { IMAGES, PHONE, PHONE_HREF, HERO_STATS } from '../data/content';

const ICONS = { Plane, Crown, Car, CalendarCheck };
const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '110%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <VehicleImage src={IMAGES.hero} alt="ALAS Velocity luxury vehicle" label="ALAS VELOCITY" className="h-full w-full" />
        <div className="absolute inset-0 bg-black/75 md:bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-14 pt-40 lg:px-10">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, letterSpacing: '0.6em' }}
          animate={{ opacity: 1, letterSpacing: '0.35em' }}
          transition={{ duration: 1.2, delay: 0.2, ease: EASE }}
          className="text-xs font-medium uppercase text-gold"
        >
          Miami Luxury Vehicle Rentals
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display mt-6 text-5xl leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
          <MaskedLine delay={0.35}>Luxury &amp; Exotic</MaskedLine>
          <MaskedLine delay={0.47}>
            Rentals in <span className="italic text-gold">Miami</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.85, ease: EASE }}
          className="mt-7 max-w-xl text-base font-light leading-relaxed text-white/70 sm:text-lg"
        >
          Experience concierge-level luxury vehicle rentals with premium service, airport delivery, and one of Miami's finest luxury fleets.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-reserve-button"
            onClick={() => onNavigate('#reserve')}
            className="group flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink shadow-[0_0_25px_rgba(200,168,93,0.25)] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_35px_rgba(200,168,93,0.4)]"
          >
            Reserve Now
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-fleet-button"
            onClick={() => onNavigate('#fleet')}
            className="border border-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold/10 hover:border-gold"
          >
            View Fleet
          </button>
          <a
            data-testid="hero-call-button"
            href={PHONE_HREF}
            className="flex items-center gap-2 px-4 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white/85 transition-colors duration-300 hover:text-gold"
          >
            <Phone className="h-4 w-4 text-gold" /> {PHONE}
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 md:grid-cols-4"
        >
          {HERO_STATS.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.div
                key={s.label}
                data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.3 + i * 0.12, ease: EASE }}
                className="flex items-center gap-3"
              >
                <Icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <span className="text-sm font-light tracking-wide text-white/80">{s.label}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
